import { useMemo } from 'react';

import { useGetApiV1ClassroomdisciplineSharedClassStudents } from '@/shared/api/generated';

import type { TutorClassOption } from '../types/tutorAi';
import { mapLookupOptions } from '../utils/mapLookupOptions';
import { LOOKUP_TAKE } from '../utils/tutorAiLimits';

export interface UseTutorClassListArgs {
  readonly search: string;
}

/**
 * Listagem de turmas para a tela de turmas do tutor.
 *
 * O lookup compartilhado **não pagina** — só aceita `Take`. Por isso o retorno expõe
 * `isTruncated`: quando a resposta bate exatamente no teto, existem provavelmente mais
 * turmas do que a tela mostra, e o operador precisa refinar a busca em vez de paginar.
 * O cálculo usa o tamanho da resposta **crua**, não da lista mapeada: `mapLookupOptions`
 * descarta itens sem `key`, então medir a lista mapeada subestimaria o truncamento sempre
 * que um item sem `key` estivesse na página cheia.
 *
 * Diferente dos autocompletes de filtro, aqui a busca **não** exige mínimo de caracteres:
 * a tela precisa listar turmas ao abrir, sem o operador digitar nada.
 *
 * @param args - Texto de busca
 * @returns Turmas encontradas, se o resultado foi truncado, estados de carregamento/erro
 * e `refetch` para nova tentativa
 *
 * @example
 * ```tsx
 * const { classes, isTruncated } = useTutorClassList({ search: 'DS' });
 * ```
 */
export function useTutorClassList({ search }: UseTutorClassListArgs) {
  const query = useGetApiV1ClassroomdisciplineSharedClassStudents({
    Take: LOOKUP_TAKE,
    ...(search ? { Search: search } : {}),
  });

  const classes: readonly TutorClassOption[] = useMemo(
    () =>
      mapLookupOptions(query.data).map(option => ({
        classStudentId: Number(option.value),
        label: option.label,
      })),
    [query.data],
  );

  return {
    classes,
    isLoading: query.isLoading,
    isError: query.isError,
    isTruncated: (query.data?.length ?? 0) >= LOOKUP_TAKE,
    refetch: query.refetch,
  } as const;
}
